import React from "react";
import styled from "@emotion/styled/macro";
import { 
    Grid2 as Grid,
    Typography,
    Tooltip
} from "@mui/material";
import LeadershipIcon from './assets/leadership_icon.webp';
import AdaptabilityIcon from './assets/adaptability_icon.webp';
import TenacityIcon from './assets/tenacity_icon.webp';
import ContinuousImprovementIcon from './assets/continuous_improvement_icon.webp';
import CollaborationIcon from './assets/collaboration_icon.webp';
import AgilityIcon from './assets/agility_icon.webp';

const StyledGridContainer = styled(Grid)`
    height: 100%;
    align-items: center;
    font-family: poppins;
    min-width: 200px;
    margin: 1vh auto;
    margin-bottom: 35px;
`;
const responsiveGridContainerStyle = {
    marginBottom: {
        xs: "35px",
        sm: "35px",
        md: "35px",
        lg: "35px",
    }
};
const StyledPrinciplesGridItem = styled(Grid)`
`;
const StyledPrinciplesHeading = styled(Typography)`
    font-family: poppins;
    font-weight: bold;
    font-size: 24px;
    text-align: center;
    margin: 3.5vh auto;
    margin-bottom: 6vh;
    pointer-events: none;
`;
const StyledPrinciplesGridBodyItem = styled(Grid)`
    text-align: center;
    margin-bottom: 40px;
    height: 250px;
`;
const responsiveGridBodyItemStyle = {
    marginTop: {
        xs: "0",
        sm: "0",
        md: "0",
    },
    marginBottom: {
        xs: "0",
        sm: "0",
        md: "40px",
        lg: "40px",
    },
    height: {
        xs: "100%",
        sm: "100%",
        md: "250px",
        lg: "250px"
    }
};
const responsiveGridBodyItemBottomRowStyle = {
    marginTop: {
        xs: "0",
        sm: "0",
        md: "30vh",
        lg: "10vh",
        xl: "2.5vh"
    },
    marginBottom: {
        xs: "0",
        sm: "5vh",
        md: "10vh",
        lg: "5vh",
        xl: "2.5vh",
    },
    height: {
        xs: "100%",
        sm: "100%",
        md: "250px",
        lg: "250px"
    }
};
const StyledPrinciplesIcon = styled.img`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    transition: all 0.25s;

    &:hover {
        scale: 1.10;
        cursor: zoom-in;
    }
`;
const StyledPrinciplesSubtitle = styled(Typography)`
    font-family: poppins;
    text-align: center;
    font-weight: bold;
    pointer-events: none;
    `;
    const StyledPrinciplesBody = styled(Typography)`
    font-family: poppins;
    text-align: center;
    font-weight: 300;
    color: gray;
    margin-bottom: 5vh;
    margin-left: 20px;
    margin-right: 20px;
    pointer-events: none;
`;

const PrinciplesSummary = () => {
    return (
        <StyledGridContainer container rowSpacing={2} sx={responsiveGridContainerStyle}>
            <StyledPrinciplesGridItem size={12}>
                <StyledPrinciplesHeading>My Guiding Principles</StyledPrinciplesHeading>
            </StyledPrinciplesGridItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:3}} sx={responsiveGridBodyItemStyle}>
                <Tooltip title="Click for some examples of my leadership!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={LeadershipIcon} alt="logo depicting leadership"/>
                </Tooltip>
                <StyledPrinciplesSubtitle>
                    Leadership
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`I lead by example and through show of action. I determine the best way forward in any circumstance and in everything that I do, positively influencing those around me and driving change.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:3}} sx={responsiveGridBodyItemStyle}>
                <Tooltip title="Click for some examples of my continuous improvement!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={ContinuousImprovementIcon} alt="logo depicting continuous improvement"/>
                </ Tooltip>
                <StyledPrinciplesSubtitle>
                    Continuous Improvement
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`My greatest asset and most treasured principle. Every day is a chance for me to prove I am a better man compared to yesterday. As a Disciple of Technology, I endeavor to continue improving my skills in every area whether it is directly related to my field or not. I apply the same philosophies to my projects as well.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:3}} sx={responsiveGridBodyItemStyle}>
                <Tooltip title="Click for some examples of my adaptability!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={AdaptabilityIcon} alt="logo depicting adaptability"/>
                </Tooltip>
                <StyledPrinciplesSubtitle>
                    Adaptability
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`I've adopted the mentality of working at start-ups and thus will never leave it at the phrase "well, that's not my job" when I can do more. I wear many hats and that is where I am the most comfortable, and I adapt and overcome any situation by upskilling or reframing.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:3}} sx={responsiveGridBodyItemStyle}>
                <Tooltip title="Click for some examples of my tenacity!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={TenacityIcon} alt="logo depicting tenacity"/>
                </Tooltip>
                <StyledPrinciplesSubtitle>
                    Tenacity
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`In the technology industry it is a constant test of will and challenge of ideas. I hold dear to me my ability to endure hardships and with every set back I seek to improve and return a sterner individual.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:6}} sx={responsiveGridBodyItemBottomRowStyle}>
                <Tooltip title="Click for some of my examples of collaboration!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={CollaborationIcon} alt="logo depicting collaboration"/>
                </Tooltip>
                <StyledPrinciplesSubtitle>
                    Collaboration
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`Many great minds working together will trounce a single great mind in most scenarios and thus I believe in collborating with and consulting those who are my colleagues and others to bounce and test my ideas so the final result will be tried, true and resilient.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
            <StyledPrinciplesGridBodyItem size={{xs:12, sm:12, md:6}} sx={responsiveGridBodyItemBottomRowStyle}>
                <Tooltip title="Click for some examples of my agility!" placement="top-start" followCursor>
                    <StyledPrinciplesIcon src={AgilityIcon} alt="logo depicting agility"/>
                </Tooltip>
                <StyledPrinciplesSubtitle>
                    Agility
                </StyledPrinciplesSubtitle>
                <StyledPrinciplesBody>
                {`Since young age I'd always had the ability to very rapidly pick up new skills and learn new tricks. In adult age I've taken this agility even further by meshing it into my every day life with learning in general. I learn things very rapidly, though I keep in mind that true understanding comes at a slower pace. There are use cases for both slow and agile paces.`}
                </StyledPrinciplesBody>
            </StyledPrinciplesGridBodyItem>
        </StyledGridContainer>
    )
}
 
export default PrinciplesSummary;